/**
 * Session form (tutoring).
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toLocalDateString } from '@/utils/format-date-utils';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DatePicker } from '@/components/forms/DatePicker';
import { TimePicker } from '@/components/forms/TimePicker';
import { Select } from '@/components/forms/Select';
import { Checkbox } from '@/components/forms/Checkbox';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { Textarea } from '@/components/forms/Textarea';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useAuthStore } from '@/stores/authStore';
import { listScoped as listStudentsRemoteScoped } from '@/services/remote/students.service';
import { listScoped as listTutorsRemoteScoped } from '@/services/remote/tutors.service';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { getSessionTypesForTab } from '@/utils/common-utils';
import {
  SESSION_REMOTE_FORM_QUERY_KEYS,
  SESSION_REMOTE_FORM_ROLES,
  SESSION_REMOTE_RECURRENCE_SELECT_OPTIONS,
} from '@/components/sessions/session-configs';
import { useCreateSession, useUpdateSession } from '@/hooks/useSessionsRemote';
import { useCreatePairOneOffSession } from '@/hooks/usePairs';
import { useFormValidation } from '@/hooks/useFormValidation';
import {
  createSessionRemoteValidationSchema,
  editSessionRemoteValidationSchema,
} from '@/utils/validation-schemas-utils';
import {
  addOneHour,
  buildPairOneOffUpdateDto,
  buildCreateSessionDto,
  buildCreateValidationContext,
  buildEditValidationContext,
  buildManagedByOptions,
  buildUpdateDto,
  computeParticipantIds,
  createManagedByOptions,
  defaultManagedById,
  getMinTimeForDate,
  initializeFormState,
  isPairOneOffSession,
  tutoringCreateFormChrome,
  isCoordinatorRole,
  isRecurringRecurrenceType,
  isTutoringType,
  mapScopedUserToOption,
  mergeTutorSituationRecords,
  todayDateString,
  withExistingChipOption,
  withPrefillOption,
} from '@/components/sessions/remote/session-form-utils';
import { UpdateRecurrenceScopeModal } from '@/components/sessions/remote/UpdateRecurrenceScopeModal';
import type { RecurrenceUpdateScope } from '@/types/inperson/session.types';
import type { ParticipantChip, SessionFormRecord } from '@/types/remote/session.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel } from '@/utils/role-label-utils';

/** Remote tab this form creates and edits. */
const SESSION_TAB = 'tutoring' as const;

/** Props for {@link TutoringSessionForm} (`create` vs `edit` discriminated). */
export type TutoringSessionFormProps =
  | {
      mode: 'create';
      onClose: () => void;
      onSuccess: () => void;
      onError?: (error: unknown) => void;
      initialTutorId?: string;
      initialTutorLabel?: string;
      initialStudentId?: string;
      initialStudentLabel?: string;
      initialSessionDate?: string;
      initialStartTime?: string;
      initialEndTime?: string;
      initialProgramId?: string;
      /** Active pair UUID — one-off create via `/binomes/:id/sessions` (ZDCD-1092). */
      pairId?: string;
      initialManagedById?: string;
      initialManagedByLabel?: string;
    }
  | {
      mode: 'edit';
      session: SessionFormRecord;
      sessionId: string;
      onClose: () => void;
      onSuccess: () => void;
    };

/** Session form (tutoring). */
export const TutoringSessionForm: React.FC<TutoringSessionFormProps> = (props) => {
  if (props.mode === 'edit') {
    return <EditTutoringSessionForm {...props} />;
  }
  return <CreateTutoringSessionForm {...props} />;
};

/** Create form for a remote tutoring session. */
function CreateTutoringSessionForm({
  onClose,
  onSuccess,
  onError,
  initialTutorId,
  initialTutorLabel,
  initialStudentId,
  initialStudentLabel,
  initialSessionDate,
  initialStartTime,
  initialEndTime,
  initialProgramId,
  pairId,
  initialManagedById,
  initialManagedByLabel,
}: Readonly<Extract<TutoringSessionFormProps, { mode: 'create' }>>) {
  const createMutation = useCreateSession();
  const createOneOffMutation = useCreatePairOneOffSession();
  const currentUser = useAuthStore((state) => state.user);
  const roles = useAuthStore((state) => state.roles);
  const isCoordinator = isCoordinatorRole(roles);
  const validation = useFormValidation(createSessionRemoteValidationSchema);
  const isFromPair = Boolean(pairId);
  const isPrefilledFromMatching = Boolean((initialTutorId || initialStudentId) && !pairId);
  const createChrome = tutoringCreateFormChrome({ isFromPair, isPrefilledFromMatching });
  const availableTypes = getSessionTypesForTab(SESSION_TAB);
  const defaultType = availableTypes[0];

  const { data: programsData } = useProgramsForMode();
  const programs = programsData?.data ?? [];

  const [selectedType, setSelectedType] = React.useState(
    isPrefilledFromMatching || isFromPair ? 'individual_tutoring' : defaultType?.type || '',
  );
  const [programId, setProgramId] = React.useState(initialProgramId ?? '');
  const [sessionDate, setSessionDate] = React.useState(initialSessionDate ?? '');
  const [startTime, setStartTime] = React.useState(initialStartTime ?? '');
  const [endTime, setEndTime] = React.useState(initialEndTime ?? '');
  const [managedById, setManagedById] = React.useState<string | null>(
    isFromPair ? (initialManagedById ?? null) : defaultManagedById(isCoordinator, currentUser?.id),
  );
  const [tutorId, setTutorId] = React.useState(initialTutorId ?? '');
  const [studentId, setStudentId] = React.useState(initialStudentId ?? '');
  const [recurrenceType, setRecurrenceType] = React.useState(createChrome.recurrenceType);
  const [planDuringHolidays, setPlanDuringHolidays] = React.useState(false);
  const [internalNotes, setInternalNotes] = React.useState('');

  const [tutorSearchQuery, setTutorSearchQuery] = React.useState('');
  const { data: tutorsData, isLoading: tutorsLoading } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.tutoringTutors(tutorSearchQuery),
    queryFn: () =>
      listTutorsRemoteScoped({
        search: tutorSearchQuery,
        tabView: 'tutors',
        pageSize: 20,
        userStatus: 'active',
        tutorSituations: ['awaiting_student', 'tutoring_in_progress'],
      }),
  });
  const onTutorSearch = React.useCallback((q: string) => setTutorSearchQuery(q), []);
  const tutorOptions = React.useMemo(() => {
    const base = (tutorsData?.data || []).map((t) => mapScopedUserToOption(t.user, t.userId));
    return withPrefillOption(base, initialTutorId, initialTutorLabel);
  }, [tutorsData?.data, initialTutorId, initialTutorLabel]);

  const [studentSearchQuery, setStudentSearchQuery] = React.useState('');
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.tutoringStudents(studentSearchQuery),
    queryFn: () =>
      listStudentsRemoteScoped({
        search: studentSearchQuery,
        tabView: 'students',
        pageSize: 20,
        studentSituation: 'awaiting_tutor',
        userStatus: 'active',
      }),
  });
  const onStudentSearch = React.useCallback((q: string) => setStudentSearchQuery(q), []);
  const studentOptions = React.useMemo(() => {
    const base = (studentsData?.data || []).map((s) => mapScopedUserToOption(s.user, s.userId));
    return withPrefillOption(base, initialStudentId, initialStudentLabel);
  }, [studentsData?.data, initialStudentId, initialStudentLabel]);

  const {
    options: managedByRawOptions,
    isLoading: managedByLoading,
    onSearch: onManagedBySearch,
  } = useAdminAutocomplete({
    roleCodes: [SESSION_REMOTE_FORM_ROLES.coordinator],
  });
  const managedByOptions = React.useMemo(() => {
    const base = buildManagedByOptions(managedByRawOptions, managedById, currentUser);
    return withPrefillOption(base, initialManagedById, initialManagedByLabel);
  }, [managedByRawOptions, managedById, currentUser, initialManagedById, initialManagedByLabel]);

  const validationCtx = buildCreateValidationContext({
    selectedType,
    sessionDate,
    startTime,
    tutorId,
    studentId,
    managedById,
    recurrenceType,
    endTime,
    programId,
    eventType: '',
    sessionType: SESSION_TAB,
    isPrefilledFromMatching: isPrefilledFromMatching || isFromPair,
    animatorsCount: 0,
    singleAnimatorId: '',
  });

  React.useEffect(() => {
    for (const field of Object.keys(createSessionRemoteValidationSchema)) {
      validation.validateField(
        field,
        validationCtx[field as keyof typeof validationCtx],
        validationCtx,
      );
    }
  }, [
    selectedType,
    sessionDate,
    startTime,
    tutorId,
    studentId,
    managedById,
    recurrenceType,
    endTime,
    programId,
  ]);

  const todayStr = todayDateString();
  /** Changing start time fills end with start + 1h when end is empty. */
  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (value) setEndTime(addOneHour(value));
  };

  /** True when the one-off slot fields are filled (pair path validates only these). */
  const isOneOffSlotValid = Boolean(sessionDate && startTime && endTime);

  /** Validate, then POST create (sessions or pair one-off). */
  const handleSubmit = async () => {
    if (isFromPair && pairId) {
      if (!isOneOffSlotValid) return;
      try {
        await createOneOffMutation.mutateAsync({
          pairId,
          dto: { sessionDate, startTime, endTime },
        });
        toast.success('Séance créée avec succès !');
        onSuccess();
      } catch (err: unknown) {
        toast.error(getErrorMessage(err));
      }
      return;
    }

    if (!validation.validateForm(validationCtx)) return;

    const mapping = availableTypes.find((t) => t.type === selectedType);
    if (!mapping) return;

    const dto = buildCreateSessionDto({
      mapping,
      sessionType: SESSION_TAB,
      sessionDate,
      startTime,
      endTime,
      recurrenceType,
      planDuringHolidays,
      tutorId,
      studentId,
      selectedTutorParticipants: [],
      selectedStudentParticipants: [],
      singleAnimatorId: '',
      selectedAnimators: [],
      managedById,
      maxParticipants: 0,
      internalNotes,
      eventType: '',
      location: '',
      budgetPosts: [],
      etpCount: 0,
      etpUnitCost: 0,
      budgetGrandTotal: 0,
      programId,
    });

    try {
      await createMutation.mutateAsync(dto as never);
      if (!isPrefilledFromMatching) {
        toast.success('Séance créée avec succès !');
      }
      onSuccess();
    } catch (err: unknown) {
      if (isPrefilledFromMatching) {
        onError?.(err);
      } else {
        toast.error(getErrorMessage(err));
      }
    }
  };

  const showType = availableTypes.length > 1;
  const showProgram = programs.length > 0;

  /** One-off from an active pair: only the slot is editable; the rest comes from the pair. */
  if (isFromPair) {
    return (
      <SlidePanelForm
        formType="create"
        title={createChrome.title}
        subtitle={createChrome.subtitle}
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Créer"
        isSubmitting={createOneOffMutation.isPending}
        isValid={isOneOffSlotValid}
        compact
      >
        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold" style={{ color: 'var(--color-2-nuance-1)' }}>
            Planification
          </h3>
          <DatePicker
            label="Date"
            required
            value={sessionDate}
            onChange={setSessionDate}
            minDate={todayStr}
            usePortal
          />
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Heure de début"
              required
              value={startTime}
              onChange={handleStartTimeChange}
              minTime={getMinTimeForDate(sessionDate, todayStr)}
              step={1}
              usePortal
            />
            <TimePicker
              label="Heure de fin"
              required
              value={endTime}
              onChange={setEndTime}
              minTime={startTime || undefined}
              step={1}
              usePortal
            />
          </div>
        </div>
      </SlidePanelForm>
    );
  }

  return (
    <SlidePanelForm
      formType="create"
      title={createChrome.title}
      subtitle={createChrome.subtitle}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="space-y-6 p-6">
        {!isPrefilledFromMatching && (showType || showProgram) && (
          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-1-nuance-3)' }}
          >
            <h3 className="font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
              Configuration de la séance
            </h3>
            {showType && (
              <Select
                label="Type de séance"
                value={selectedType}
                onChange={setSelectedType}
                options={availableTypes.map((t) => ({ value: t.type, label: t.label }))}
              />
            )}
            {showProgram && (
              <div>
                <span className="block text-sm font-medium mb-2">
                  Programme <span className="text-red-500">*</span>
                </span>
                <SegmentedControl
                  value={programId}
                  onChange={setProgramId}
                  options={programs.map((p) => ({ value: p.id, label: p.name ?? p.id }))}
                  size="sm"
                />
              </div>
            )}
          </div>
        )}

        {!isPrefilledFromMatching && (
          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-1-nuance-3)' }}
          >
            <h3 className="font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
              Participants
            </h3>
            <Autocomplete
              label="Tuteur"
              required
              placeholder="Rechercher un tuteur..."
              options={tutorOptions}
              value={tutorId}
              onChange={setTutorId}
              onSearch={onTutorSearch}
              isLoading={tutorsLoading}
              minSearchLength={2}
              helperText="Seuls les tuteurs actifs en attente d'élève ou déjà en cours de tutorat sont affichés."
            />
            <Autocomplete
              label="Élève"
              required
              placeholder="Rechercher un élève..."
              options={studentOptions}
              value={studentId}
              onChange={setStudentId}
              onSearch={onStudentSearch}
              isLoading={studentsLoading}
              minSearchLength={2}
              helperText="Seuls les élèves actifs en attente tuteur sont affichés."
            />
          </div>
        )}

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold" style={{ color: 'var(--color-2-nuance-1)' }}>
            Planification
          </h3>
          <DatePicker
            label="Date"
            required
            value={sessionDate}
            onChange={setSessionDate}
            minDate={todayStr}
          />
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Heure de début"
              required
              value={startTime}
              onChange={handleStartTimeChange}
              minTime={getMinTimeForDate(sessionDate, todayStr)}
              step={1}
            />
            <TimePicker
              label="Heure de fin"
              required
              value={endTime}
              onChange={setEndTime}
              minTime={startTime || undefined}
              step={1}
            />
          </div>
          <Select
            label="Récurrence"
            required
            value={recurrenceType}
            onChange={setRecurrenceType}
            options={SESSION_REMOTE_RECURRENCE_SELECT_OPTIONS}
          />
          <Checkbox
            id="plan-holidays"
            checked={planDuringHolidays}
            onCheckedChange={(checked) => setPlanDuringHolidays(Boolean(checked))}
            label="Disponible pendant les vacances scolaires"
          />
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <Autocomplete
            label="Géré par"
            required
            placeholder="Sélectionner un coordinateur"
            options={managedByOptions}
            value={managedById || ''}
            onChange={(val) => setManagedById(val || null)}
            onSearch={onManagedBySearch}
            isLoading={managedByLoading}
            minSearchLength={2}
          />
        </div>

        <div
          className="space-y-2 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <Textarea
            label="Notes internes (optionnel)"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notes ou commentaires internes..."
            rows={3}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
}

/** Edit form for an existing remote tutoring session. */
function EditTutoringSessionForm({
  session,
  sessionId,
  onClose,
  onSuccess,
}: Readonly<Extract<TutoringSessionFormProps, { mode: 'edit' }>>) {
  const updateMutation = useUpdateSession();
  const validation = useFormValidation(editSessionRemoteValidationSchema);
  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [recurrenceType, setRecurrenceType] = React.useState('');
  const [scopeModalOpen, setScopeModalOpen] = React.useState(false);
  const [planDuringHolidays, setPlanDuringHolidays] = React.useState(false);
  const [internalNotes, setInternalNotes] = React.useState('');
  const [managedById, setManagedById] = React.useState<string | null>(null);
  const [programId, setProgramId] = React.useState('');
  const [selectedTutor, setSelectedTutor] = React.useState<ParticipantChip | null>(null);
  const [selectedStudent, setSelectedStudent] = React.useState<ParticipantChip | null>(null);

  const { data: programsResponse } = useProgramsForMode();
  const programs = programsResponse?.data ?? [];

  const [tutorSearchQuery, setTutorSearchQuery] = React.useState('');
  const { data: tutorsData, isLoading: isLoadingTutors } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.tutoringTutors(tutorSearchQuery),
    queryFn: async () => {
      const base = {
        search: tutorSearchQuery,
        tabView: 'tutors' as const,
        pageSize: 20,
        userStatus: 'active' as const,
      };
      const [awaitingStudent, tutoringInProgress] = await Promise.all([
        listTutorsRemoteScoped({ ...base, tutorSituation: 'awaiting_student' }),
        listTutorsRemoteScoped({ ...base, tutorSituation: 'tutoring_in_progress' }),
      ]);
      const data = mergeTutorSituationRecords(awaitingStudent.data, tutoringInProgress.data);
      return { data, total: data.length };
    },
  });
  const onSearchTutors = React.useCallback((q: string) => setTutorSearchQuery(q), []);
  const tutorOptions = React.useMemo(
    () =>
      withExistingChipOption(
        (tutorsData?.data || []).map((t) => mapScopedUserToOption(t.user, t.userId)),
        selectedTutor,
      ),
    [tutorsData?.data, selectedTutor],
  );

  const [studentSearchQuery, setStudentSearchQuery] = React.useState('');
  const { data: studentsData, isLoading: isLoadingStudents } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.tutoringStudents(studentSearchQuery),
    queryFn: () =>
      listStudentsRemoteScoped({
        search: studentSearchQuery,
        tabView: 'students',
        pageSize: 20,
        studentSituation: 'awaiting_tutor',
        userStatus: 'active',
      }),
  });
  const onSearchStudents = React.useCallback((q: string) => setStudentSearchQuery(q), []);
  const studentOptions = React.useMemo(
    () =>
      withExistingChipOption(
        (studentsData?.data || []).map((s) => mapScopedUserToOption(s.user, s.userId)),
        selectedStudent,
      ),
    [studentsData?.data, selectedStudent],
  );

  const {
    options: managedByOptions,
    isLoading: managedByLoading,
    onSearch: onManagedBySearch,
  } = useAdminAutocomplete({ roleCodes: [SESSION_REMOTE_FORM_ROLES.coordinator] });
  const effectiveManagedByOptions = React.useMemo(
    () => createManagedByOptions(managedByOptions, session.managedBy),
    [managedByOptions, session.managedBy],
  );

  React.useEffect(() => {
    initializeFormState(session, {
      setSessionDate,
      setStartTime,
      setEndTime,
      setRecurrenceType,
      setPlanDuringHolidays,
      setInternalNotes,
      setMaxParticipants: () => undefined,
      setSelectedTutor,
      setSelectedStudent,
      setSingleAnimatorId: () => undefined,
      setSingleAnimatorName: () => undefined,
      setSelectedAnimators: () => undefined,
      setSelectedTutorParticipants: () => undefined,
      setSelectedStudentParticipants: () => undefined,
      setManagedById,
      setProgramId,
      setEventType: () => undefined,
      setLocation: () => undefined,
      setBudgetPosts: () => undefined,
      setEtpUnitCost: () => undefined,
    });
    const locked = isPairOneOffSession(session);
    validation.validateInitialFormData(
      locked
        ? {
            sessionDate: toLocalDateString(new Date(session.sessionDate)),
            startTime: session.startTime,
          }
        : {
            sessionDate: toLocalDateString(new Date(session.sessionDate)),
            startTime: session.startTime,
            managedById: session.managedBy?.id || null,
            programId: session.programId ?? '',
            _isTutoring: true,
          },
    );
  }, [session]);

  const isTutoring = isTutoringType(session.type);
  const participantsLocked = isPairOneOffSession(session);
  React.useEffect(() => {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById,
      programId,
      isTutoring,
    });
    validation.validateField('sessionDate', sessionDate, ctx);
    validation.validateField('startTime', startTime, ctx);
    if (!participantsLocked) {
      validation.validateField('managedById', managedById, ctx);
      validation.validateField('programId', programId, ctx);
    }
  }, [sessionDate, startTime, managedById, programId, isTutoring, participantsLocked]);

  const isRecurring = isRecurringRecurrenceType(session.recurrenceType);

  async function submitUpdate(updateFutureOccurrences: boolean): Promise<void> {
    const dto = participantsLocked
      ? buildPairOneOffUpdateDto({
          sessionDate,
          startTime,
          endTime,
          planDuringHolidays,
          internalNotes,
        })
      : buildUpdateDto({
          session,
          sessionDate,
          startTime,
          endTime,
          recurrenceType,
          planDuringHolidays,
          internalNotes,
          maxParticipants: 0,
          ...computeParticipantIds({
            session,
            isTutoring,
            selectedTutor,
            selectedStudent,
            selectedTutorParticipants: [],
            selectedStudentParticipants: [],
            selectedAnimators: [],
            singleAnimatorId: '',
          }),
          managedById,
          programId,
          eventType: '',
          location: '',
          budgetPosts: [],
          etpCount: 0,
          etpUnitCost: 0,
          budgetGrandTotal: 0,
          updateFutureOccurrences,
        });

    try {
      await updateMutation.mutateAsync({ id: sessionId, dto });
      toast.success('Séance modifiée avec succès !');
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
      throw err;
    }
  }

  async function handleSubmit(): Promise<void> {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById,
      programId,
      isTutoring,
    });
    // Pair one-off: only the slot is editable — skip managedBy/program required checks.
    if (participantsLocked) {
      validation.validateField('sessionDate', sessionDate, ctx);
      validation.validateField('startTime', startTime, ctx);
      if (!sessionDate || !startTime) return;
    } else if (!validation.validateForm(ctx)) {
      return;
    }
    if (isRecurring && !participantsLocked) {
      setScopeModalOpen(true);
      return;
    }
    try {
      await submitUpdate(false);
    } catch {
      return;
    }
  }

  async function handleConfirmScope(scope: RecurrenceUpdateScope): Promise<void> {
    await submitUpdate(scope === 'following');
  }

  function handleCloseScopeModal(): void {
    setScopeModalOpen(false);
  }

  return (
    <>
      <SlidePanelForm
        formType="edit"
        title="Modifier la séance de tutorat"
        subtitle={
          participantsLocked
            ? 'Séance ponctuelle issue d’un binôme : seuls la date et les horaires sont modifiables'
            : 'Modifiez les informations de la séance de tutorat'
        }
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Enregistrer"
        isSubmitting={updateMutation.isPending}
        isValid={validation.isFormValid()}
      >
        <div className="space-y-6 p-6">
          {programs.length > 0 && (
            <div
              className="space-y-4 p-4 rounded-lg border"
              style={{ borderColor: 'var(--color-1-nuance-3)' }}
            >
              <h3 className="font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
                Configuration de la séance
              </h3>
              <div>
                <span className="block text-sm font-medium mb-2">
                  Programme <span className="text-red-500">*</span>
                </span>
                <SegmentedControl
                  value={programId}
                  onChange={setProgramId}
                  options={programs.map((p) => ({ value: p.id, label: p.name }))}
                  size="sm"
                  disabled={participantsLocked}
                />
              </div>
            </div>
          )}

          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-2-nuance-3)' }}
          >
            <h3 className="font-semibold text-lg" style={{ color: 'var(--color-2-nuance-1)' }}>
              Planification
            </h3>
            <DatePicker label="Date" value={sessionDate} onChange={setSessionDate} required />
            <div className="grid grid-cols-2 gap-4">
              <TimePicker
                label="Heure de début"
                value={startTime}
                onChange={setStartTime}
                required
                step={1}
              />
              <TimePicker label="Heure de fin" value={endTime} onChange={setEndTime} step={1} />
            </div>
            <Select
              label="Récurrence"
              value={recurrenceType}
              onChange={setRecurrenceType}
              options={SESSION_REMOTE_RECURRENCE_SELECT_OPTIONS}
              disabled
            />
            <Checkbox
              id="plan-holidays"
              checked={planDuringHolidays}
              onCheckedChange={(checked) => setPlanDuringHolidays(Boolean(checked))}
              label="Disponible pendant les vacances scolaires"
              disabled={participantsLocked}
            />
          </div>

          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-1-nuance-3)' }}
          >
            <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
              Participants
            </h3>
            <Autocomplete
              label={getRoleLabel('tutor')}
              placeholder="Rechercher un tuteur..."
              options={tutorOptions}
              value={selectedTutor?.id || ''}
              onChange={(val) => {
                const option = tutorOptions.find((o) => o.value === val);
                setSelectedTutor(
                  option ? { id: option.value, name: option.label, role: 'tutor' } : null,
                );
              }}
              onSearch={onSearchTutors}
              isLoading={isLoadingTutors}
              minSearchLength={2}
              helperText="Seuls les tuteurs actifs en attente d'élève ou déjà en cours de tutorat sont affichés."
              disabled={participantsLocked}
            />
            <Autocomplete
              label={getRoleLabel('student')}
              placeholder="Rechercher un élève..."
              options={studentOptions}
              value={selectedStudent?.id || ''}
              onChange={(val) => {
                const option = studentOptions.find((o) => o.value === val);
                setSelectedStudent(
                  option ? { id: option.value, name: option.label, role: 'student' } : null,
                );
              }}
              onSearch={onSearchStudents}
              isLoading={isLoadingStudents}
              minSearchLength={2}
              helperText="Seuls les élèves actifs en attente tuteur sont affichés."
              disabled={participantsLocked}
            />
            <Autocomplete
              label="Géré par"
              placeholder="Sélectionner un coordinateur"
              options={effectiveManagedByOptions}
              value={managedById || ''}
              onChange={(val) => setManagedById(val || null)}
              onSearch={onManagedBySearch}
              isLoading={managedByLoading}
              minSearchLength={2}
              required
              disabled={participantsLocked}
            />
          </div>

          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-1-nuance-3)' }}
          >
            <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
              Notes internes
            </h3>
            <Textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Notes ou commentaires internes..."
              rows={3}
            />
          </div>
        </div>
      </SlidePanelForm>
      <UpdateRecurrenceScopeModal
        open={scopeModalOpen}
        onClose={handleCloseScopeModal}
        onConfirm={handleConfirmScope}
        isSubmitting={updateMutation.isPending}
      />
    </>
  );
}
